import React, { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { CONTACT_EMAIL, FEEDBACK_SURVEY_URL } from '@/utils/constants'
import { ButtonSize } from '@/components/atoms/Button/types'
import { useDropdownClickOutside } from '@/components/common/Dropdown/useDropdownClickOutside'
import { useAppWalkthrough } from '@/hooks/useAppWalkthrough/useAppWalkthrough'
import { resetWalkthroughState } from '@/utils/appWalkthroughStorage'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { UserType } from '@/hooks/useApi/types'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'

type FeedbackButtonConfig = {
    name: string
    icon: string
    label: string
    onClick: () => void
    iconSize?: ButtonSize
    className?: string
}

export const HelpAndFeedbackButton: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false)
    const { t } = useTranslation('translations')
    const { startWalkthrough } = useAppWalkthrough()
    const user = useAuthStore((state) => state.user)
    const customer = useAuthStore((state) => state.customer)

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) setIsOpen(false)
    })

    const shouldShowWalkthrough = useMemo(() => {
        if (!user) return false
        if (user.type === UserType.CUSTOMER) return true
        // For agent/broker, only show if customer is selected
        return customer !== null
    }, [user, customer])

    const startWalkthroughWithReset = useCallback(() => {
        resetWalkthroughState()
        setIsOpen(false)
        void startWalkthrough(1)
    }, [startWalkthrough])

    const handleFeedbackSurvey = () => {
        window.open(FEEDBACK_SURVEY_URL)
        setIsOpen(false)
    }

    const handleContactUs = () => {
        window.open(`mailto:${CONTACT_EMAIL}`)
        setIsOpen(false)
    }

    const handleAppWalkthrough = () => {
        const {
            hasUnsavedChanges,
            setOnNavigationConfirmHandler,
            onNavigationBlocked,
            clearNavigationIntent,
        } = useNavigationGuardStore.getState()

        if (hasUnsavedChanges) {
            // Set handler to be called if user confirms
            setOnNavigationConfirmHandler(() => {
                startWalkthroughWithReset()
                clearNavigationIntent()
            })

            // Show the unsaved changes modal
            if (onNavigationBlocked) {
                onNavigationBlocked()
            }

            setIsOpen(false)
            return
        }

        // No unsaved changes, start immediately
        startWalkthroughWithReset()
    }

    const handleClose = () => {
        setIsOpen(false)
    }

    const feedbackButtons: FeedbackButtonConfig[] = [
        {
            name: 'quick-feedback-survey-button',
            icon: 'assignment',
            label: t('help_and_feedback_widget.feedback_button'),
            onClick: handleFeedbackSurvey,
            iconSize: 'small',
            className: '!rounded-full',
        },
        {
            name: 'contact-us-button',
            icon: 'mail',
            label: t('help_and_feedback_widget.contact_button'),
            onClick: handleContactUs,
            iconSize: 'small',
            className: '!rounded-full',
        },
        ...(shouldShowWalkthrough
            ? [
                  {
                      name: 'app-walkthrough-button',
                      icon: 'signpost',
                      label: t('help_and_feedback_widget.walkthrough_button'),
                      onClick: handleAppWalkthrough,
                      iconSize: 'small' as ButtonSize,
                      className: '!rounded-full',
                  },
              ]
            : []),
        {
            name: 'close-help-and-feedback-button',
            icon: 'close',
            label: '',
            onClick: handleClose,
            className: 'h-12 w-12 !rounded-full',
        },
    ]

    return (
        <div
            ref={dropdownRef}
            className="fixed bottom-8 right-8 z-50 flex flex-col items-end gap-2"
        >
            {!isOpen ? (
                <ButtonContainer>
                    <Button
                        buttonRef={buttonRef}
                        name="help-and-feedback-button"
                        size="xlarge"
                        variant="outlineSecondary"
                        icon="help"
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setIsOpen(true)
                        }}
                        className="!rounded-full"
                        iconClassName="mt-0.5"
                    >
                        {t('help_and_feedback_widget.help_and_feedback_button')}
                    </Button>
                </ButtonContainer>
            ) : (
                <>
                    {feedbackButtons.map((btn) => (
                        <ButtonContainer key={btn.name}>
                            <Button
                                name={btn.name}
                                icon={btn.icon}
                                size="xlarge"
                                variant="outlineSecondary"
                                iconSize={btn.iconSize}
                                className={btn.className}
                                onClick={btn.onClick}
                                iconClassName="mt-0.5"
                            >
                                {btn.label}
                            </Button>
                        </ButtonContainer>
                    ))}
                </>
            )}
        </div>
    )
}

const ButtonContainer: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => <div className="rounded-full shadow-lg">{children}</div>
